import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PhoneMockup } from '@/pages/Discovery/components/PhoneMockup';
import { getPublicGeniesForIndustry } from '@/data/discovery/genie-catalog';
import { getBrandForPersona } from '@/data/genies/persona-brands';
import type { PublicGeniePersona } from '@/types/discovery-catalog';

interface SolutionDemoGeniesProps {
  industrySlug: string;
  industryName: string;
}

const INITIAL_COUNT = 4;

/**
 * Builds a diverse list of genies for the showcase.
 * 1. One featured genie per subcategory (diversity first)
 * 2. One non-featured genie per remaining subcategory
 * 3. Remaining genies by order to fill more slots
 */
const pickRepresentativeGenies = (genies: PublicGeniePersona[]): PublicGeniePersona[] => {
  const pickedIds = new Set<string>();
  const picks: PublicGeniePersona[] = [];
  const seenSubs = new Set<string>();

  // Pass 1: one featured genie per subcategory
  for (const g of genies) {
    if (g.isFeatured && !seenSubs.has(g.subcategoryId)) {
      picks.push(g);
      pickedIds.add(g.id);
      seenSubs.add(g.subcategoryId);
    }
  }

  // Pass 2: one genie per remaining subcategory
  for (const g of genies) {
    if (!seenSubs.has(g.subcategoryId)) {
      picks.push(g);
      pickedIds.add(g.id);
      seenSubs.add(g.subcategoryId);
    }
  }

  // Pass 3: fill with remaining genies (featured first, then by order)
  const remaining = genies
    .filter(g => !pickedIds.has(g.id))
    .sort((a, b) => {
      if (a.isFeatured !== b.isFeatured) return a.isFeatured ? -1 : 1;
      return (a.order || 99) - (b.order || 99);
    });

  for (const g of remaining) {
    picks.push(g);
  }

  return picks;
};

export const SolutionDemoGenies = ({ industrySlug, industryName }: SolutionDemoGeniesProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const allIndustryGenies = getPublicGeniesForIndustry(industrySlug);
  const allRepresentative = pickRepresentativeGenies(allIndustryGenies);
  const hasMore = allRepresentative.length > INITIAL_COUNT;
  const displayGenies = expanded ? allRepresentative : allRepresentative.slice(0, INITIAL_COUNT);

  const getLogoPath = (personaId: string): string | null => {
    const brand = getBrandForPersona(personaId);
    if (!brand) return null;
    const safeName = brand.brandName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `/brand-logos/${safeName}-stacked.png`;
  };

  if (allRepresentative.length === 0) return null;

  return (
    <section className="w-full py-16 sm:py-20 md:py-24 relative overflow-hidden bg-gradient-to-b from-slate-50/80 via-white to-white">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-200/50 to-transparent" />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mb-4">
            Try a {industryName} Genie Demo
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Click any genie below to start a live voice demo and see how it could work for your business.
          </p>
        </div>

        {/* Phone mockup gallery */}
        <div className="flex flex-wrap justify-center gap-5 lg:gap-8 max-w-[1400px] mx-auto">
          {displayGenies.map((genie) => {
            const brand = getBrandForPersona(genie.id);
            return (
              <div key={genie.id} className="flex flex-col items-center gap-3">
                {/* Featured badge */}
                {genie.isFeatured && (
                  <Badge className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-[10px] px-2.5 py-0.5 border-0">
                    Popular
                  </Badge>
                )}
                {!genie.isFeatured && <div className="h-5" />}

                <PhoneMockup
                  genie={genie}
                  brand={brand}
                  logoPath={getLogoPath(genie.id)}
                  onClick={() => navigate(`/discover/demo/${genie.id}`)}
                  variant="text"
                />

                {/* Name + role */}
                <div className="text-center max-w-[240px]">
                  <h3 className="text-base font-bold text-gray-900 leading-tight">{genie.name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{genie.role}</p>
                </div>

                {/* CTA */}
                <Button
                  size="sm"
                  onClick={() => navigate(`/discover/demo/${genie.id}`)}
                  className="bg-primary hover:bg-primary/90 text-white text-sm font-medium"
                >
                  Try Demo
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* Show more / less toggle */}
        {hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {expanded ? (
                <>Show less <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Show {allRepresentative.length - INITIAL_COUNT} more <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}

        {/* Hint text */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Click any phone to start a live voice conversation
        </p>
      </div>
    </section>
  );
};
